import { inject } from '@angular/core';
import { ApolloError } from '@apollo/client/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  filter,
  tap,
} from 'rxjs';


import { BlueSageQueueActions } from '../bluesage/bluesage-queue.actions';
import { BlueSageRefundLegActions } from '../bluesage/bluesage-refund-leg.actions';
import { BlueSageSettingsActions } from '../bluesage/bluesage-settings.actions';
import { BlueSageRefundDialogActions } from '../bluesage/refund-dialog/bluesage-refund-dialog.actions';
import { BlueSageTerminalPickerActions } from '../bluesage/terminal-picker/bluesage-terminal-picker.actions';
import { BlueSageTerminalPushActions } from '../bluesage/terminal-push/bluesage-terminal-push.actions';
import { JobActivityActions } from '../job/job-activity/job-activity.actions';
import { CloseJobComponentActions } from '../job/job-create/close-job-component/close-job-component.actions';
import { JobCreateCustomerActions } from '../job/job-create/job-create-customer/job-create-customer.actions';
import { JobCreateLocationsActions } from '../job/job-create/job-create-locations/job-create-locations-state.actions';
import { JobCreateActions } from '../job/job-create/job-create-state/job-create.actions';
import { JobFormActionButtonActions } from '../job/job-create/job-form-action-button/job-form-action-button.actions';
import { JobEstimateActions } from '../job/job-estimate/job-estimate.actions';
import { JobFinancialsActions } from '../job/job-financials/job-financials.actions';
import { JobHeaderActions } from '../job/job-header/job-header.actions';
import { JobStatusDropdownActions } from '../job/job-header/job-status-dropdown/job-status-dropdown.actions';
import { JobTagActions } from '../job/job-header/job-tags/job-tags.actions';
import { InventoryActions } from '../job/job-inventory/job-inventory.actions';
import { JobInvoiceActions } from '../job/job-invoice/job-invoice.actions';
import { JobAgentActions } from '../job/job-overview/overview-header/job-agent/job-agent.actions';
import { JobLocationsOverviewActions } from '../job/job-overview/overview-locations/overview-locations.actions';
import { OverviewTimelineActions } from '../job/job-overview/overview-timeline/overview-timeline.actions';
import { EventItineraryActions } from '../job/job-overview/overview-timeline/timeline-book-event/event-itinerary/event-itinerary.actions';
import { BookEventDialogActions } from '../job/job-overview/overview-timeline/timeline-book-event/timeline-book-event.actions';
import { CreateEventDialogActions } from '../job/job-overview/overview-timeline/timeline-create-event/timeline-create-event.actions';
import { JobToolActions } from '../job/job-tool.actions';
import { JobMutateObjectActions } from '../job/mutate-object/mutate-object.actions';
import { NewOpportunityActions } from '../new-opportunity/new-opportunity.actions';
import { quickJsActions } from '../quickjs/quickjs.actions';
import { DispatchCalendarRemarksDialogActions } from '../schedules/dispatch/components/dispatch-calendar-remarks-dialog/dispatch-calendar-remarks-dialog.actions';
import { FreyaNotificationsService } from '../services/freya-notifications.service';
import { CancelAndCloseDialogActions } from '../shared/cancel-and-close-dialog/cancel-and-close-dialog.actions';
import { CreateDocumentDialogActions } from '../shared/create-document-dialog/create-document-dialog.actions';
import { DocumentTypesActions } from '../shared/create-document-dialog/document-types';
import { CancelPayLinkDialogActions } from '../transactions/cancel-pay-link-dialog/cancel-pay-link-dialog.actions';
import { CancelPreauthDialogActions } from '../transactions/cancel-preauth-dialog/cancel-preauth-dialog.actions';
import { CreateTransactionDialogActions } from '../transactions/create-transaction-dialog/create-transaction-dialog.actions';
import { UserPageMergeConflictDialogActions } from '../users/user-page/user-page-merge-conflict-dialog/user-page-merge-conflict-dialog.actions';

const successActions = [
    JobToolActions.saveChangesSuccess,
    quickJsActions.saveSharedCrewPaySuccess,
    JobToolActions.updateJobAreaSuccess,
    JobToolActions.importJobToCurrentZoneSuccess,
    JobToolActions.deleteJobSuccess,
    CloseJobComponentActions.closeJobSuccess,
    JobHeaderActions.openJobSuccess,
    JobStatusDropdownActions.moveToNextStageSuccess,
    JobFinancialsActions.removeDamageSuccess,
    JobEstimateActions.sendDocumentSuccess,
    CreateDocumentDialogActions.resendSucceeded,
    JobTagActions.setTagsSuccess,
    JobAgentActions.assignAgentSuccess,
    JobAgentActions.unassignAgentSuccess,
    OverviewTimelineActions.updateEventStatusSuccess,
    OverviewTimelineActions.deleteEventSuccess,
    JobCreateCustomerActions.updateCustomerDataSuccess,
    JobCreateLocationsActions.updateLocationsSuccess,
    JobLocationsOverviewActions.addJobLocationSuccess,
    JobLocationsOverviewActions.deleteJobLocationSuccess,
    EventItineraryActions.addLocationToJobSuccess,
    EventItineraryActions.saveEventChangesSuccess,
    JobFormActionButtonActions.updateJobSuccess,
    CreateEventDialogActions.createEventSuccess,
    CreateEventDialogActions.updateEventSuccess,
    BookEventDialogActions.bookEventSuccess,
    BookEventDialogActions.rescheduleEventSuccess,
    JobCreateActions.createJobSuccess,
    InventoryActions.createSelfSurveyLinkSuccess,
    JobToolActions.updateEventLocationsSuccess,
    JobToolActions.autoAssignRolesSuccess,
    NewOpportunityActions.createJobSuccess,
    JobInvoiceActions.createInvoiceSuccess,
    JobInvoiceActions.finalizeInvoiceSuccess,
    CancelAndCloseDialogActions.cancelEventsSuccess,
    CancelAndCloseDialogActions.cancelAndCloseSuccess,
    quickJsActions.useCalculatorFromZoneSettingsSuccess,
    JobActivityActions.updateHeartbeatSuccess,
    DispatchCalendarRemarksDialogActions.saveRemarksSuccess,
    BlueSageSettingsActions.saveCredentialsSuccess,
    BlueSageSettingsActions.saveLocationSuccess,
    BlueSageSettingsActions.setEnabledSuccess,
    BlueSageQueueActions.attachSuccess,
    BlueSageQueueActions.completeHoldSuccess,
    BlueSageQueueActions.dismissSuccess,
    BlueSageRefundDialogActions.refundSuccess,
    BlueSageRefundLegActions.refundConfirmed,
    BlueSageRefundLegActions.releaseConfirmed,
    CreateTransactionDialogActions.recordPaymentSuccess,
    CreateTransactionDialogActions.unsentTransactionCancelled,
    CreateTransactionDialogActions.chargeCardSuccess,
    CreateTransactionDialogActions.squareCheckoutLinkSent,
    CreateTransactionDialogActions.deleteCardOnFileSuccess,
    CancelPayLinkDialogActions.cancelPayLinkSuccess,
    CancelPreauthDialogActions.cancelPreauthSuccess,
    UserPageMergeConflictDialogActions.mergeUsersSuccess,
];

const successActionTypes = successActions.map((a) => a.type);

const successMessages: Record<typeof successActionTypes[number], string> = {
    [BlueSageSettingsActions.saveCredentialsSuccess.type]: 'BlueSage credentials saved for this franchise',
    [BlueSageSettingsActions.saveLocationSuccess.type]: 'BlueSage location saved for this zone',
    [BlueSageSettingsActions.setEnabledSuccess.type]: 'BlueSage payment type updated',
    [BlueSageQueueActions.attachSuccess.type]: 'Payment attached to the job',
    [BlueSageQueueActions.completeHoldSuccess.type]: 'Authorization completed with this payment',
    [BlueSageQueueActions.dismissSuccess.type]: 'Payment dismissed',
    // Requirement: a BlueSage refund is SENT when the request returns and DONE
    // when its leg settles, so these two say different things on purpose.
    [BlueSageRefundDialogActions.refundSuccess.type]: 'Refund sent',
    [BlueSageRefundLegActions.refundConfirmed.type]: 'Refund confirmed',
    [BlueSageRefundLegActions.releaseConfirmed.type]: 'Pre-auth released',
    [CreateTransactionDialogActions.recordPaymentSuccess.type]: 'Payment recorded on the job',
    [CreateTransactionDialogActions.unsentTransactionCancelled.type]:
        'Nothing was charged — the draft payment was cancelled on the job',
    [CreateTransactionDialogActions.chargeCardSuccess.type]: 'Card charged',
    [CreateTransactionDialogActions.squareCheckoutLinkSent.type]: 'Square Checkout link sent to the customer',
    [CreateTransactionDialogActions.deleteCardOnFileSuccess.type]: 'Card removed',
    [CancelPayLinkDialogActions.cancelPayLinkSuccess.type]: 'Pay link cancelled',
    [CancelPreauthDialogActions.cancelPreauthSuccess.type]:
        'Release sent — waiting for the terminal to confirm',
    [JobToolActions.saveChangesSuccess.type]: 'Changes saved',
    [quickJsActions.saveSharedCrewPaySuccess.type]: 'Crew pay saved',
    [JobToolActions.updateJobAreaSuccess.type]: 'Job area changed manually',
    [JobToolActions.importJobToCurrentZoneSuccess.type]: 'Job imported to current zone',
    [JobToolActions.deleteJobSuccess.type]: 'Job deleted successfully',
    [CloseJobComponentActions.closeJobSuccess.type]: 'Job closed',
    [JobHeaderActions.openJobSuccess.type]: 'Job opened',
    [JobStatusDropdownActions.moveToNextStageSuccess.type]: 'Job stage updated',
    [JobFinancialsActions.removeDamageSuccess.type]: 'Damage deleted',
    [JobEstimateActions.sendDocumentSuccess.type]: 'Document sent successfully',
    [CreateDocumentDialogActions.resendSucceeded.type]: 'Document sent',
    [JobTagActions.setTagsSuccess.type]: 'Tags assigned successfully',
    [JobAgentActions.assignAgentSuccess.type]: 'Agent assigned',
    [JobAgentActions.unassignAgentSuccess.type]: 'Agent unassigned',
    [OverviewTimelineActions.updateEventStatusSuccess.type]: 'Event status updated',
    [OverviewTimelineActions.deleteEventSuccess.type]: 'Event deleted',
    [JobCreateCustomerActions.updateCustomerDataSuccess.type]: 'Customer updated successfully',
    [JobCreateLocationsActions.updateLocationsSuccess.type]: 'Job locations updated successfully',
    [JobLocationsOverviewActions.addJobLocationSuccess.type]: 'Job locations updated successfully',
    [JobLocationsOverviewActions.deleteJobLocationSuccess.type]: 'Location deleted',
    [EventItineraryActions.addLocationToJobSuccess.type]: 'Location added',
    [EventItineraryActions.saveEventChangesSuccess.type]: 'Changes Saved',
    [JobFormActionButtonActions.updateJobSuccess.type]: 'Job Updated',
    [CreateEventDialogActions.createEventSuccess.type]: 'Event created successfully',
    [CreateEventDialogActions.updateEventSuccess.type]: 'Event updated successfully',
    [BookEventDialogActions.bookEventSuccess.type]: 'Event booked',
    [BookEventDialogActions.rescheduleEventSuccess.type]: 'Event booked',
    [JobCreateActions.createJobSuccess.type]: 'Job created',
    [InventoryActions.createSelfSurveyLinkSuccess.type]: 'Self-survey link created successfully and sent to customer.',
    [JobToolActions.updateEventLocationsSuccess.type]: 'Event locations updated to match job',
    [JobToolActions.autoAssignRolesSuccess.type]: 'Roles assigned',
    [NewOpportunityActions.createJobSuccess.type]: 'Job created',
    [JobInvoiceActions.createInvoiceSuccess.type]: 'Generating invoice',
    [JobInvoiceActions.finalizeInvoiceSuccess.type]: 'Invoice finalized',
    [CancelAndCloseDialogActions.cancelEventsSuccess.type]: 'Events cancelled',
    [CancelAndCloseDialogActions.cancelAndCloseSuccess.type]: 'Events cancelled and job closed',
    [quickJsActions.useCalculatorFromZoneSettingsSuccess.type]: 'Calculator updated from zone settings',
    [JobActivityActions.updateHeartbeatSuccess.type]: 'Heartbeat Updated',
    [DispatchCalendarRemarksDialogActions.saveRemarksSuccess.type]: 'Calendar remarks saved',
    [UserPageMergeConflictDialogActions.mergeUsersSuccess.type]: 'Customers merged successfully',
}

const errorActions = [
    JobToolActions.saveChangesError,
    quickJsActions.saveSharedCrewPayError,
    JobToolActions.updateJobAreaError,
    JobToolActions.importJobToCurrentZoneError,
    JobToolActions.deleteJobError,
    CloseJobComponentActions.closeJobError,
    JobHeaderActions.openJobError,
    JobToolActions.fetchJobError,
    JobToolActions.buildSaveChangesVariablesError,
    JobToolActions.autoAssignRolesError,
    JobStatusDropdownActions.moveToNextStageError,
    JobFinancialsActions.removeDamageError,
    JobEstimateActions.sendDocumentError,
    JobTagActions.searchTagsError,
    JobTagActions.setTagsError,
    JobAgentActions.assignAgentError,
    JobAgentActions.unassignAgentError,
    OverviewTimelineActions.updateEventStatusError,
    OverviewTimelineActions.deleteEventError,
    JobCreateCustomerActions.searchUsersError,
    JobCreateCustomerActions.updateCustomerDataError,
    JobCreateLocationsActions.updateLocationsError,
    JobLocationsOverviewActions.addJobLocationError,
    JobLocationsOverviewActions.deleteJobLocationError,
    JobCreateLocationsActions.resolveServiceAreaError,
    EventItineraryActions.addLocationToJobError,
    EventItineraryActions.saveEventChangesError,
    JobFormActionButtonActions.updateJobError,
    JobFormActionButtonActions.buildUpdateJobVariablesError,
    CreateEventDialogActions.createEventError,
    CreateEventDialogActions.updateEventError,
    BookEventDialogActions.bookEventError,
    BookEventDialogActions.rescheduleEventError,
    BookEventDialogActions.integrationsConflictConfirmationError,
    JobCreateActions.createJobError,
    InventoryActions.createSelfSurveyLinkError,
    InventoryActions.viewQubeSheetsProjectButtonFailed,
    JobToolActions.updateEventLocationsError,
    quickJsActions.runCalculationsError,
    JobMutateObjectActions.mutateButtonClickedError,
    CreateDocumentDialogActions.previewFailed,
    CreateDocumentDialogActions.createDocumentFailed,
    CreateDocumentDialogActions.resendFailed,
    DocumentTypesActions.loadError,
    NewOpportunityActions.buildCreateJobVariablesError,
    NewOpportunityActions.createJobError,
    NewOpportunityActions.downloadSummaryError,
    JobInvoiceActions.createInvoiceError,
    JobInvoiceActions.finalizeInvoiceError,
    CancelAndCloseDialogActions.fetchJobError,
    CancelAndCloseDialogActions.cancelEventsError,
    CancelAndCloseDialogActions.cancelAndCloseError,
    JobActivityActions.addCommentAttachmentsUploadError,
    JobActivityActions.replyAttachmentsUploadError,
    quickJsActions.useCalculatorFromZoneSettingsError,
    JobActivityActions.updateHeartbeatError,
    DispatchCalendarRemarksDialogActions.saveRemarksError,
    BlueSageSettingsActions.loadSetupError,
    BlueSageSettingsActions.saveCredentialsError,
    BlueSageSettingsActions.saveLocationError,
    BlueSageSettingsActions.setEnabledError,
    BlueSageQueueActions.loadPaymentsError,
    BlueSageQueueActions.loadSummaryError,
    // Requirement: every error action must be registered somewhere. `knip` and the
    // conventions reviewer both catch an action dispatched but registered nowhere
    // — that is exactly how `loadSummaryError` shipped silent on !2264.
    BlueSageTerminalPickerActions.loadTerminalsError,
    // The push panel shows the push failure itself, in place, with a Try again
    // beside it — but the ERROR action still has to be registered somewhere or it
    // is an action dispatched into nothing. `cancelError` in particular is often
    // NOT a failure (`push_not_cancellable` means the device already finished),
    // so its toast is worded to say so.
    BlueSageTerminalPushActions.pushError,
    BlueSageTerminalPushActions.completeError,
    BlueSageTerminalPushActions.cancelError,
    BlueSageTerminalPushActions.pushStatusPollError,
    BlueSageTerminalPushActions.pushStatusCheckError,
    BlueSageTerminalPushActions.inFlightPushesSettleError,
    // Same defect as `unsentTransactionCancelError` below, in the push effects:
    // dispatched into nothing. It fires when a push settled with NOTHING taken
    // and the draft row it left behind could not then be closed, so the row is
    // still on the job and silence is the one answer that helps nobody.
    BlueSageTerminalPushActions.unsettledTransactionCancelError,
    // The dialog shows the refusal in place, with the one-click full-refund
    // recovery beside it — but the action still has to be registered, or it is an
    // action dispatched into nothing.
    BlueSageRefundDialogActions.refundError,
    BlueSageRefundLegActions.refundNotConfirmed,
    BlueSageRefundLegActions.releaseNotConfirmed,
    // The dialog shows the create failure in place, but an unregistered error
    // action is an action dispatched into nothing — the class of defect `knip`
    // and the conventions reviewer both exist to catch.
    CreateTransactionDialogActions.createTransactionError,
    CreateTransactionDialogActions.loadPaymentTypeError,
    CreateTransactionDialogActions.loadPaymentTypesError,
    // Releasing a hold has FOUR distinguishable outcomes, already mapped
    // server-side. The toast carries whichever one came back rather than a
    // generic failure — one of them is a retry and one means the money was taken.
    // Phase B. `cardsOnFileError` is deliberately quiet-ish: the list degrades to
    // empty server-side, so this reports why the section is missing rather than
    // implying a payment failed.
    CreateTransactionDialogActions.cardFormError,
    CreateTransactionDialogActions.chargeCardError,
    // The cancel effect dispatches this, and an unregistered error is the defect
    // the note at the top of this list describes: the operator has to be told a
    // draft row is still on the job.
    CreateTransactionDialogActions.unsentTransactionCancelError,
    CreateTransactionDialogActions.cardsOnFileError,
    CreateTransactionDialogActions.deleteCardOnFileError,
    CancelPayLinkDialogActions.cancelPayLinkError,
    CancelPreauthDialogActions.cancelPreauthError,
    BlueSageQueueActions.attachError,
    BlueSageQueueActions.dismissError,
    BlueSageQueueActions.completeHoldError,
    UserPageMergeConflictDialogActions.mergeUsersError,
    // No matching success toast on purpose: navigator notes autosave on a debounce, so a
    // "saved" toast would fire every time the crew paused typing. A failure still has to be
    // visible — silently losing notes is the bug this exists to fix (#14).
    quickJsActions.saveNavigatorNotesError,

    // Same reasoning as navigator notes, for the rest of the navigator's persistence. Both of
    // these failed completely silently: the crew's step advance or their inventory review would
    // simply not reach the server, with nothing on screen to say so. Crews run this on phones in
    // driveways, which is exactly where the request fails.
    quickJsActions.syncNavigatorStepError,
    quickJsActions.patchNavigatorDataError,
    quickJsActions.navigatorStepUndoneError,
];

const errorMessages: Record<typeof errorActions[number]['type'], string> = {
    [BlueSageSettingsActions.loadSetupError.type]: 'Could not read the BlueSage configuration',
    [BlueSageQueueActions.loadPaymentsError.type]: 'Could not load the unmatched payments queue',
    // The badge count is its own query, so it can fail while the page loads
    // fine. Unreported, the nav would just keep showing a stale number.
    [BlueSageQueueActions.loadSummaryError.type]: 'Could not refresh the unmatched payments count',
    // A payment screen that shows no terminals must say WHY. Rendering a failed
    // roster read as an empty list tells the operator this location has no
    // devices, which is a different and wrong answer.
    [BlueSageTerminalPickerActions.loadTerminalsError.type]: 'Could not load the terminals for this location',
    [BlueSageTerminalPushActions.pushError.type]: 'Could not send that to the terminal',
    // Its own message: "could not send that to the terminal" is the wrong
    // sentence when what failed was completing a hold the card already carries.
    [BlueSageTerminalPushActions.completeError.type]: 'Could not complete that authorization',
    [BlueSageTerminalPushActions.cancelError.type]: 'Could not cancel that push',
    // A failed POLL is not a failed payment — the customer may be mid-tap. The
    // panel deliberately leaves the phase alone and the next tick may answer.
    [BlueSageTerminalPushActions.pushStatusPollError.type]: 'Lost contact while checking the terminal — still trying',
    // Same rule for the transaction-tracked poller: a failed read is not a
    // failed payment, and the next tick may answer.
    [BlueSageTerminalPushActions.pushStatusCheckError.type]: 'Lost contact while checking the payment — still trying',
    [BlueSageTerminalPushActions.inFlightPushesSettleError.type]: 'Could not check the payments this job still shows in flight',
    // Says what is TRUE of the job rather than what failed internally: nothing
    // was charged, and a draft row is still sitting there for somebody to clear.
    [BlueSageTerminalPushActions.unsettledTransactionCancelError.type]:
        'Nothing was charged, but the draft payment is still on the job — close it from the transactions list',
    [BlueSageRefundDialogActions.refundError.type]: 'The refund was refused',
    [BlueSageRefundLegActions.refundNotConfirmed.type]: 'The refund was not completed',
    [BlueSageRefundLegActions.releaseNotConfirmed.type]: 'The pre-auth was not released',
    [CreateTransactionDialogActions.createTransactionError.type]: 'Could not record the payment on this job',
    [CreateTransactionDialogActions.loadPaymentTypeError.type]: 'Could not read the BlueSage payment type for this zone',
    [CreateTransactionDialogActions.loadPaymentTypesError.type]: 'Could not read the cash and check payment types',
    [CreateTransactionDialogActions.cardFormError.type]: 'Could not open the card form',
    [CreateTransactionDialogActions.chargeCardError.type]: 'The card could not be charged',
    // Requirement: the message says what is true of the JOB — a draft row is
    // still on it and somebody has to deal with that.
    [CreateTransactionDialogActions.unsentTransactionCancelError.type]:
        'A draft payment could not be cancelled on the job — check the transactions before retrying',
    [CreateTransactionDialogActions.cardsOnFileError.type]: 'Could not load the cards on file',
    // Requirement: "delete my card" is a promise to the customer, so a failure
    // is never reported as success.
    [CreateTransactionDialogActions.deleteCardOnFileError.type]: 'That card could not be removed',
    [CancelPayLinkDialogActions.cancelPayLinkError.type]: 'Could not cancel that pay link',
    [CancelPreauthDialogActions.cancelPreauthError.type]: 'Could not cancel that pre-auth',
    // These two carry the server's own reason — "that job has no open invoice",
    // "already dismissed" — which is the actionable part, so the generic text is
    // only a fallback.
    [BlueSageQueueActions.attachError.type]: 'Could not attach the payment',
    [BlueSageQueueActions.dismissError.type]: 'Could not dismiss the payment',
    [BlueSageQueueActions.completeHoldError.type]: 'Could not complete that authorization with this payment',
    [BlueSageSettingsActions.saveCredentialsError.type]: 'Could not save the BlueSage credentials',
    [BlueSageSettingsActions.saveLocationError.type]: 'Could not save the BlueSage location',
    [BlueSageSettingsActions.setEnabledError.type]: 'Could not change the BlueSage payment type',
    [JobToolActions.buildSaveChangesVariablesError.type]: 'Failed to save changes (client error)',
    [JobToolActions.autoAssignRolesError.type]: 'Failed to auto-assign roles',
    [JobToolActions.saveChangesError.type]: 'Failed to save changes (server error)',
    [quickJsActions.saveSharedCrewPayError.type]: 'Failed to save crew pay',
    [JobToolActions.updateJobAreaError.type]: 'Failed to change job area',
    [JobToolActions.importJobToCurrentZoneError.type]: 'Failed to import job to current zone',
    [JobToolActions.deleteJobError.type]: 'Failed to delete job',
    [CloseJobComponentActions.closeJobError.type]: 'Failed to close job',
    [JobHeaderActions.openJobError.type]: 'Failed to open job',
    [JobToolActions.fetchJobError.type]: 'Failed to load job',
    [JobStatusDropdownActions.moveToNextStageError.type]: 'Failed to update job stage',
    [JobFinancialsActions.removeDamageError.type]: 'Failed to delete damage',
    [JobEstimateActions.sendDocumentError.type]: 'Failed to send document',
    [JobTagActions.searchTagsError.type]: 'Failed to load tags',
    [JobTagActions.setTagsError.type]: 'Failed to assign tags',
    [JobAgentActions.assignAgentError.type]: 'Failed to assign agent',
    [JobAgentActions.unassignAgentError.type]: 'Failed to unassign agent',
    [OverviewTimelineActions.updateEventStatusError.type]: 'Failed to update event status',
    [OverviewTimelineActions.deleteEventError.type]: 'Failed to delete event',
    [JobCreateCustomerActions.searchUsersError.type]: 'Failed to load users',
    [JobCreateCustomerActions.updateCustomerDataError.type]: 'Failed to update customer',
    [JobCreateLocationsActions.updateLocationsError.type]: 'Failed to update job locations',
    [JobLocationsOverviewActions.addJobLocationError.type]: 'Failed to update job location',
    [JobLocationsOverviewActions.deleteJobLocationError.type]: 'Failed to delete job location',
    [JobCreateLocationsActions.resolveServiceAreaError.type]: 'Failed to resolve service area',
    [EventItineraryActions.addLocationToJobError.type]: 'Error adding location',
    [EventItineraryActions.saveEventChangesError.type]: 'Error saving changes',
    [JobFormActionButtonActions.updateJobError.type]: 'Failed to update job (client error)',
    [JobFormActionButtonActions.buildUpdateJobVariablesError.type]: 'Failed to update job (server error)',
    [CreateEventDialogActions.createEventError.type]: 'Failed to create event',
    [CreateEventDialogActions.updateEventError.type]: 'Failed to update event',
    [BookEventDialogActions.bookEventError.type]: 'Failed to book event',
    [BookEventDialogActions.rescheduleEventError.type]: 'Failed to book event',
    [BookEventDialogActions.integrationsConflictConfirmationError.type]: 'Failed to show the integrations conflict confirmation',
    [JobCreateActions.createJobError.type]: 'Failed to create job',
    [InventoryActions.createSelfSurveyLinkError.type]: 'Failed to create self survey',
    [InventoryActions.viewQubeSheetsProjectButtonFailed.type]: 'Qube Sheets project not found for this job',
    [JobToolActions.updateEventLocationsError.type]: 'Failed to update event locations',
    [quickJsActions.runCalculationsError.type]: 'Failed to compute values',
    [JobMutateObjectActions.mutateButtonClickedError.type]: 'Error occurred, please try again',
    [CreateDocumentDialogActions.previewFailed.type]: 'Failed to load the document preview',
    [CreateDocumentDialogActions.createDocumentFailed.type]: 'Failed to create document',
    [CreateDocumentDialogActions.resendFailed.type]: 'Failed to send document',
    [DocumentTypesActions.loadError.type]: 'Failed to load document types',
    [NewOpportunityActions.buildCreateJobVariablesError.type]: 'Failed to create job (client error)',
    [NewOpportunityActions.createJobError.type]: 'Failed to create job',
    [NewOpportunityActions.downloadSummaryError.type]: 'Failed to download summary',
    [JobInvoiceActions.createInvoiceError.type]: 'Failed to create invoice',
    [JobInvoiceActions.finalizeInvoiceError.type]: 'Failed to finalize invoice',
    [CancelAndCloseDialogActions.fetchJobError.type]: 'Failed to load job',
    [CancelAndCloseDialogActions.cancelEventsError.type]: 'Failed to cancel events',
    [CancelAndCloseDialogActions.cancelAndCloseError.type]: 'Failed to cancel events and close job',
    [JobActivityActions.addCommentAttachmentsUploadError.type]: 'Failed to upload attachments',
    [JobActivityActions.replyAttachmentsUploadError.type]: 'Failed to upload attachments',
    [quickJsActions.useCalculatorFromZoneSettingsError.type]: 'Failed to update calculator',
    [JobActivityActions.updateHeartbeatError.type]: 'Failed to update heartbeat',
    [DispatchCalendarRemarksDialogActions.saveRemarksError.type]: 'Failed to save calendar remarks',
    [UserPageMergeConflictDialogActions.mergeUsersError.type]: 'Failed to merge customers',
    [quickJsActions.saveNavigatorNotesError.type]: 'Failed to save notes — do not close this page',
    [quickJsActions.syncNavigatorStepError.type]: 'Failed to save your progress — check your signal',
    [quickJsActions.patchNavigatorDataError.type]: 'Failed to save your progress — check your signal',
    // The crew has already been moved back on screen; the write is what failed. Says "undo"
    // rather than "save your progress" so they can tell which way the mismatch runs.
    [quickJsActions.navigatorStepUndoneError.type]: 'Failed to undo — the step may still show as completed',
}

export const broadcastActionNotifications = createEffect((
    actions$ = inject(Actions),
    notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
        ofType(
            ...successActions,
            ...errorActions,
        ),
        filter((action) => !('skipNotify' in action)),
        tap((action: Action) => {
            if (successActionTypes.includes(action.type as any)) {

                let successMessage = successMessages[action.type];

                let detail: string;

                if ('customSuccessMessage' in action && typeof action.customSuccessMessage === 'string') {
                    successMessage = action.customSuccessMessage;
                }

                if ('customSuccessDetail' in action && typeof action.customSuccessDetail === 'string') {
                    detail = action.customSuccessDetail;
                }

                notify.success(successMessage, detail);

            } else {

                const errorAction = action as ReturnType<typeof errorActions[number]>;

                let errorMessage = errorMessages[action.type];

                if ('customErrorMessage' in action) {
                    errorMessage = action.customErrorMessage;
                }

                if (errorAction.error instanceof ApolloError) {
                    notify.apolloError(errorMessage, errorAction.error);
                } else {
                    notify.error(errorMessage, errorAction.error.message);
                }
            }
        }),
    );

}, { functional: true, dispatch: false });
